'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

// 类型定义
interface FaqItem {
  question: string;
  answer: string;
  isShow?: boolean;
}

interface PageData {
  title: string;
  faq_list: FaqItem[];
}

interface Props {
  pageData: PageData;
  deviceType: EPlatform;
}

export default function FaqPart({ pageData, deviceType }: Props) {
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [faqRef] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 处理展开/收起
  // ✅ 处理展开/收起 - 使用新的埋点系统
  const handleChange = (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedItems((prev) => [...prev, index]);
    } else {
      setExpandedItems((prev) => prev.filter((item) => item !== index));
    }
  };

  // 检查是否展开
  const isExpanded = (index: number) => expandedItems.includes(index);

  // 安全检查：确保faq_list数组存在
  if (!pageData?.faq_list || !Array.isArray(pageData.faq_list)) {
    return null;
  }

  return (
    <Box
      ref={faqRef}
      className="w-full px-[14px] py-[32px] lg:px-[224px] lg:py-[50px] 2xl:px-[448px] 2xl:pt-[57px] 2xl:pb-[72px] bg-[#ECEBFC]"
    >
      <Box>
        <h3
          className="font-bb text-[20px] leading-[24px] text-center lg:text-[36px] lg:leading-[44px] 2xl:text-[48px] 2xl:leading-[58px] text-primary-text border-b border-[#D4D4D4] pb-[21px] lg:pb-[24px] 2xl:pb-[28px]"
          dangerouslySetInnerHTML={{ __html: pageData.title }}
        />

        <Box component="ul" className="space-y-[0px]">
          {pageData.faq_list.map((faq: any, index: number) => (
            <Box
              component="li"
              key={`faq-${faq.question || index}`}
              className="border-b border-[#D4D4D4] pb-[16px] lg:pb-[24px] 2xl:pb-[24px]"
            >
              <Accordion
                expanded={isExpanded(index)}
                onChange={handleChange(index)}
                sx={{
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  border: 'none',
                  margin: 0,
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<CollapseArrowDownOutlined className="text-[#A9A9A9] text-[16px]" />}
                  sx={{
                    padding: deviceType !== EPlatform.WEB ? '16px 0 0' : '24px 0 0',
                    minHeight: 'auto',
                    '& .MuiAccordionSummary-content': {
                      margin: '0 !important',
                    },
                    '&.Mui-expanded': {
                      minHeight: 'auto',
                      margin: 0,
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      transition: 'transform 0.3s ease',
                      transformOrigin: 'center',
                    },
                    '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                      transform: 'rotate(180deg)',
                    },
                  }}
                >
                  <h4
                    className="font-bb text-[14px] leading-[17px] pr-[10px] lg:text-[16px] lg:leading-[19px] 2xl:text-[16px] 2xl:leading-[19px] text-primary-text lg:pr-[16px] 2xl:pr-[16px]"
                    dangerouslySetInnerHTML={{ __html: faq.question }}
                  />
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: deviceType !== EPlatform.WEB ? '10px 0 0' : '16px 0 0',
                    backgroundColor: 'transparent',
                  }}
                >
                  <p
                    className="font-ir text-[12px] leading-[18px]"
                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
